import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { MainLayout } from '../components/layout/MainLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Badge } from '../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { toast } from 'sonner';
import { 
    Plus, 
    Edit, 
    Trash2, 
    Sparkles, 
    CheckCircle,
    BookOpen,
    RefreshCw,
    Eye,
    FileText,
    Lightbulb,
    Target,
    Clock
} from 'lucide-react';
import { ScrollArea } from '../components/ui/scroll-area';

const Topics = () => {
    const { api, isAdmin } = useAuth();
    const [topics, setTopics] = useState([]);
    const [loading, setLoading] = useState(true);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editingTopic, setEditingTopic] = useState(null);
    const [generating, setGenerating] = useState({});
    const [formData, setFormData] = useState({
        name: '',
        description: '',
        difficulty_level: 'intermediate'
    });
    const [viewingTopic, setViewingTopic] = useState(null);
    const [viewDialogOpen, setViewDialogOpen] = useState(false);

    useEffect(() => {
        fetchTopics();
    }, []);

    const fetchTopics = async () => {
        try {
            const response = await api.get('/topics');
            setTopics(response.data);
        } catch (error) {
            toast.error('Failed to load topics');
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            if (editingTopic) {
                await api.put(`/topics/${editingTopic.id}`, formData);
                toast.success('Topic updated');
            } else {
                await api.post('/topics', formData);
                toast.success('Topic created');
            }
            fetchTopics();
            resetForm();
        } catch (error) {
            toast.error('Failed to save topic');
        }
    };

    const handleDelete = async (id) => {
        if (!confirm('Are you sure you want to delete this topic?')) return;
        try {
            await api.delete(`/topics/${id}`);
            toast.success('Topic deleted');
            fetchTopics();
        } catch (error) {
            toast.error('Failed to delete topic');
        }
    };

    const generateContent = async (id) => {
        setGenerating(prev => ({ ...prev, [id]: true }));
        try {
            await api.post(`/topics/${id}/generate-content`);
            toast.success('Content generated!');
            fetchTopics();
        } catch (error) {
            toast.error('Failed to generate content');
        } finally {
            setGenerating(prev => ({ ...prev, [id]: false }));
        }
    };

    const approveTopic = async (id) => {
        try {
            await api.post(`/topics/${id}/approve`);
            toast.success('Topic approved');
            fetchTopics();
        } catch (error) {
            toast.error('Failed to approve topic');
        }
    };

    const resetForm = () => {
        setFormData({ name: '', description: '', difficulty_level: 'intermediate' });
        setEditingTopic(null);
        setDialogOpen(false);
    };

    const openEdit = (topic) => {
        setEditingTopic(topic);
        setFormData({
            name: topic.name,
            description: topic.description,
            difficulty_level: topic.difficulty_level
        });
        setDialogOpen(true);
    };

    const viewContent = (topic) => {
        setViewingTopic(topic);
        setViewDialogOpen(true);
    };

    const getDifficultyColor = (level) => {
        switch(level) {
            case 'beginner': return 'bg-emerald-500/10 text-emerald-500';
            case 'intermediate': return 'bg-yellow-500/10 text-yellow-500';
            case 'advanced': return 'bg-red-500/10 text-red-500';
            default: return 'bg-muted text-muted-foreground';
        }
    };

    if (loading) {
        return (
            <MainLayout>
                <div className="flex items-center justify-center h-[60vh]">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary" />
                </div>
            </MainLayout>
        );
    }

    return (
        <MainLayout>
            <div className="space-y-6 animate-fade-in" data-testid="topics-page">
                <div className="flex items-center justify-between">
                    <div>
                        <h1 className="text-3xl font-heading font-bold tracking-tight">Topics</h1>
                        <p className="text-muted-foreground mt-1">Manage training topics</p>
                    </div>
                    <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                        <DialogTrigger asChild>
                            <Button className="btn-primary" data-testid="create-topic-btn">
                                <Plus className="h-4 w-4 mr-2" />
                                Create Topic
                            </Button>
                        </DialogTrigger>
                        <DialogContent>
                            <DialogHeader>
                                <DialogTitle>{editingTopic ? 'Edit Topic' : 'Create Topic'}</DialogTitle>
                            </DialogHeader>
                            <form onSubmit={handleSubmit} className="space-y-4">
                                <div className="space-y-2">
                                    <Label htmlFor="name">Topic Name</Label>
                                    <Input
                                        id="name"
                                        value={formData.name}
                                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                        required
                                        data-testid="topic-name-input"
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="description">Description</Label>
                                    <Textarea
                                        id="description"
                                        value={formData.description}
                                        onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                                        required
                                        data-testid="topic-description-input"
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label>Difficulty Level</Label>
                                    <Select
                                        value={formData.difficulty_level}
                                        onValueChange={(value) => setFormData({ ...formData, difficulty_level: value })}
                                    >
                                        <SelectTrigger data-testid="topic-difficulty-select">
                                            <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="beginner">Beginner</SelectItem>
                                            <SelectItem value="intermediate">Intermediate</SelectItem>
                                            <SelectItem value="advanced">Advanced</SelectItem>
                                        </SelectContent>
                                    </Select>
                                </div>
                                <div className="flex gap-2 pt-4">
                                    <Button type="button" variant="outline" onClick={resetForm} className="flex-1">
                                        Cancel
                                    </Button>
                                    <Button type="submit" className="flex-1" data-testid="save-topic-btn">
                                        {editingTopic ? 'Update' : 'Create'}
                                    </Button>
                                </div>
                            </form>
                        </DialogContent>
                    </Dialog>
                </div>

                <Card>
                    <CardContent className="p-0">
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHead>Topic</TableHead>
                                    <TableHead>Difficulty</TableHead>
                                    <TableHead>Status</TableHead>
                                    <TableHead>Content</TableHead>
                                    <TableHead className="text-right">Actions</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {topics.map((topic) => (
                                    <TableRow key={topic.id} data-testid={`topic-row-${topic.id}`}>
                                        <TableCell>
                                            <div className="flex items-center gap-3">
                                                <div className="p-2 rounded-lg bg-primary/10">
                                                    <BookOpen className="h-4 w-4 text-primary" />
                                                </div>
                                                <div>
                                                    <p className="font-medium">{topic.name}</p>
                                                    <p className="text-sm text-muted-foreground line-clamp-1">
                                                        {topic.description}
                                                    </p>
                                                </div>
                                            </div>
                                        </TableCell>
                                        <TableCell>
                                            <Badge className={getDifficultyColor(topic.difficulty_level)}>
                                                {topic.difficulty_level}
                                            </Badge>
                                        </TableCell>
                                        <TableCell>
                                            <Badge variant={topic.status === 'approved' ? 'default' : 'secondary'}>
                                                {topic.status}
                                            </Badge>
                                        </TableCell>
                                        <TableCell>
                                            {topic.ai_content ? (
                                                <Button
                                                    variant="ghost"
                                                    size="sm"
                                                    className="gap-1 text-emerald-600 hover:text-emerald-700"
                                                    onClick={() => viewContent(topic)}
                                                    data-testid={`view-content-${topic.id}`}
                                                >
                                                    <Eye className="h-4 w-4" />
                                                    View
                                                </Button>
                                            ) : (
                                                <Badge variant="outline">Pending</Badge>
                                            )}
                                        </TableCell>
                                        <TableCell className="text-right">
                                            <div className="flex items-center justify-end gap-2">
                                                <Button
                                                    variant="ghost"
                                                    size="icon"
                                                    onClick={() => generateContent(topic.id)}
                                                    disabled={generating[topic.id]}
                                                    title="Generate AI Content"
                                                    data-testid={`generate-content-${topic.id}`}
                                                >
                                                    {generating[topic.id] ? (
                                                        <RefreshCw className="h-4 w-4 animate-spin" />
                                                    ) : (
                                                        <Sparkles className="h-4 w-4 text-secondary" />
                                                    )}
                                                </Button>
                                                {isAdmin && topic.status !== 'approved' && (
                                                    <Button
                                                        variant="ghost"
                                                        size="icon"
                                                        onClick={() => approveTopic(topic.id)}
                                                        title="Approve Topic"
                                                        data-testid={`approve-topic-${topic.id}`}
                                                    >
                                                        <CheckCircle className="h-4 w-4 text-emerald-500" />
                                                    </Button>
                                                )}
                                                <Button
                                                    variant="ghost"
                                                    size="icon"
                                                    onClick={() => openEdit(topic)}
                                                    data-testid={`edit-topic-${topic.id}`}
                                                >
                                                    <Edit className="h-4 w-4" />
                                                </Button>
                                                <Button
                                                    variant="ghost"
                                                    size="icon"
                                                    onClick={() => handleDelete(topic.id)}
                                                    data-testid={`delete-topic-${topic.id}`}
                                                >
                                                    <Trash2 className="h-4 w-4 text-destructive" />
                                                </Button>
                                            </div>
                                        </TableCell>
                                    </TableRow>
                                ))}
                                {topics.length === 0 && (
                                    <TableRow>
                                        <TableCell colSpan={5} className="text-center py-8 text-muted-foreground">
                                            No topics found. Create your first topic!
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </CardContent>
                </Card>

                {/* View Content Dialog */}
                <Dialog open={viewDialogOpen} onOpenChange={setViewDialogOpen}>
                    <DialogContent className="max-w-3xl max-h-[85vh]">
                        <DialogHeader>
                            <DialogTitle className="flex items-center gap-2">
                                <BookOpen className="h-5 w-5 text-primary" />
                                {viewingTopic?.name}
                            </DialogTitle>
                        </DialogHeader>
                        
                        {viewingTopic && (
                            <ScrollArea className="h-[65vh] pr-4">
                                <div className="space-y-6">
                                    {/* Description */}
                                    <div className="p-4 rounded-lg bg-muted/50 border">
                                        <p className="text-sm text-muted-foreground">
                                            {viewingTopic.description}
                                        </p>
                                        <div className="flex gap-2 mt-3">
                                            <Badge className={getDifficultyColor(viewingTopic.difficulty_level)}>
                                                {viewingTopic.difficulty_level}
                                            </Badge>
                                            <Badge variant={viewingTopic.status === 'approved' ? 'default' : 'secondary'}>
                                                {viewingTopic.status}
                                            </Badge>
                                        </div>
                                    </div>

                                    {/* AI Generated Content */}
                                    {viewingTopic.ai_content && (
                                        <div className="space-y-4">
                                            <div className="flex items-center gap-2">
                                                <Sparkles className="h-5 w-5 text-yellow-500" />
                                                <h3 className="font-semibold">AI-Generated Content</h3>
                                            </div>
                                            <div className="prose prose-sm max-w-none dark:prose-invert">
                                                <div className="whitespace-pre-wrap text-sm leading-relaxed p-4 rounded-lg bg-card border">
                                                    {viewingTopic.ai_content}
                                                </div>
                                            </div>
                                        </div>
                                    )}

                                    {/* Key Concepts */}
                                    {viewingTopic.key_concepts && viewingTopic.key_concepts.length > 0 && (
                                        <div className="space-y-3">
                                            <div className="flex items-center gap-2">
                                                <Lightbulb className="h-5 w-5 text-amber-500" />
                                                <h3 className="font-semibold">Key Concepts</h3>
                                            </div>
                                            <div className="grid gap-2">
                                                {viewingTopic.key_concepts.map((concept, idx) => (
                                                    <div key={idx} className="flex items-start gap-2 p-3 rounded-lg bg-amber-500/5 border border-amber-500/20">
                                                        <span className="text-amber-500 font-medium">{idx + 1}.</span>
                                                        <span className="text-sm">{concept}</span>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}

                                    {/* Key Takeaways */}
                                    {viewingTopic.key_takeaways && viewingTopic.key_takeaways.length > 0 && (
                                        <div className="space-y-3">
                                            <div className="flex items-center gap-2">
                                                <Target className="h-5 w-5 text-emerald-500" />
                                                <h3 className="font-semibold">Key Takeaways</h3>
                                            </div>
                                            <ul className="space-y-2">
                                                {viewingTopic.key_takeaways.map((takeaway, idx) => (
                                                    <li key={idx} className="flex items-start gap-2 text-sm">
                                                        <CheckCircle className="h-4 w-4 text-emerald-500 mt-0.5 flex-shrink-0" />
                                                        {takeaway}
                                                    </li>
                                                ))}
                                            </ul>
                                        </div>
                                    )}

                                    {/* Practice Scenarios */}
                                    {viewingTopic.practice_scenarios && viewingTopic.practice_scenarios.length > 0 && (
                                        <div className="space-y-3">
                                            <div className="flex items-center gap-2">
                                                <FileText className="h-5 w-5 text-blue-500" />
                                                <h3 className="font-semibold">Practice Scenarios</h3>
                                            </div>
                                            <div className="space-y-3">
                                                {viewingTopic.practice_scenarios.map((scenario, idx) => (
                                                    <div key={idx} className="p-3 rounded-lg bg-blue-500/5 border border-blue-500/20">
                                                        <p className="text-sm font-medium mb-1">Scenario {idx + 1}</p>
                                                        <p className="text-sm text-muted-foreground">{scenario}</p>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}

                                    {/* Timestamps */}
                                    <div className="flex items-center gap-4 text-xs text-muted-foreground pt-4 border-t">
                                        <span className="flex items-center gap-1">
                                            <Clock className="h-3 w-3" />
                                            Created: {new Date(viewingTopic.created_at).toLocaleDateString()}
                                        </span>
                                        {viewingTopic.updated_at && (
                                            <span>
                                                Updated: {new Date(viewingTopic.updated_at).toLocaleDateString()}
                                            </span>
                                        )}
                                    </div>
                                </div>
                            </ScrollArea>
                        )}
                    </DialogContent>
                </Dialog>
            </div>
        </MainLayout>
    );
};

export default Topics;
